import { Component, OnInit, OnDestroy, HostListener, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatBadgeModule } from '@angular/material/badge';
import { MatDialog } from '@angular/material/dialog';
import { Subject, takeUntil, debounceTime, distinctUntilChanged, filter } from 'rxjs';
import { DateTime } from 'luxon';

import { DocumentsFiltersPanelComponent } from '../components/documents-filters-panel/documents-filters-panel.component';
import { DocumentRowComponent } from '../components/document-row/document-row.component';
import { 
    DocumentsFilterService, 
    DocumentFilters, 
    DocumentStats,
    VIEW_DOCUMENT_TYPES,
    NON_KSEF_DOCUMENT_TYPES,
    SortField,
    SortOption
} from 'shared/services/documents-filter.service';
import { DataSynchronizerService } from 'shared/services/data-synchronization.service';
import { SynchronizationService } from 'shared/services/synchronization.service';
import { DbService } from 'shared/services/db.service';
import { KSeFApiService } from 'shared/services/api/ksef.api.service';
import { DocumentsApiService } from 'shared/services/api/documents.api.service';
import { KsefActionsService } from 'shared/services/ksef-actions.service';
import { InvoicesService } from 'shared/services/invoices.service';
import { SettingsService } from 'shared/services/settings.service';
import { SnackbarService } from 'shared/services/snackbar.service';
import { 
    Invoice, 
    AppDb, 
    AppData,
    ReceivedPaymentStatus, 
    DocumentHelper,
    DocumentLike,
    InvoiceGenerator,
    ObjectUtils,
    UserSettings,
    Utils
} from 'fakturnia-shared';
import { settings } from 'environments/environment';
import { SelectLanguageDialogComponent } from 'shared/dialogs/select-language-dialog/select-language-dialog.component';

// Rozszerzony typ dokumentu z nowymi polami (do czasu pełnego typowania)
type DocumentWithUnifiedFields = any;

@Component({
    selector: 'app-documents-list',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        MatProgressSpinnerModule,
        MatBadgeModule,
        DocumentsFiltersPanelComponent,
        DocumentRowComponent
    ],
    templateUrl: './documents-list.component.html',
    styleUrls: ['./documents-list.component.scss']
})
export class DocumentsListComponent implements OnInit, OnDestroy {
    
    private _destroy$ = new Subject<void>();
    private _db: AppDb | null = null;
    
    /** Indeks pierwszego widocznego wiersza do przywrócenia po powrocie z podglądu */
    private _scrollRestoreIndex: number | null = null;
    
    /** Timeout do automatycznego czyszczenia podświetlenia nowych dokumentów */
    private _highlightTimeout: any = null;
    
    /** Ustawienia użytkownika (logo, subskrypcja) — do generowania PDF */
    private _userSettings: UserSettings | null = null;
    
    @ViewChild('documentsContainer') documentsContainer!: ElementRef<HTMLDivElement>;
    
    documents: DocumentWithUnifiedFields[] = [];
    isLoading = false;
    isSyncing = false;
    
    /** ID dokumentów nowo zsynchronizowanych — do animacji podświetlenia */
    highlightedDocumentIds = new Set<string>();
    
    // Panel filtrów
    isFilterPanelOpen = true;
    isMobile = false;
    
    // Paginacja
    limit = 25;
    total = 0;
    
    // Statystyki
    stats: DocumentStats = {
        all: 0,
        drafts: 0,
        sales: 0,
        purchases: 0,
        warehouse: 0,
        other: 0,
        pendingKsef: 0,
        overdue: 0
    };

    /** Czy funkcje KSeF są włączone w ustawieniach użytkownika */
    get enableKsefFeatures(): boolean {
        return this._userSettings?.settings?.general?.enableKsefFeatures ?? true;
    }
    
    constructor(
        private _filterService: DocumentsFilterService,
        private _dataSynchronizerService: DataSynchronizerService,
        private _synchronizationService: SynchronizationService,
        private _dbService: DbService,
        private _ksefApiService: KSeFApiService,
        private _documentsApiService: DocumentsApiService,
        private _ksefActionsService: KsefActionsService,
        private _invoicesService: InvoicesService,
        private _settingsService: SettingsService,
        private _snackbarService: SnackbarService,
        private _route: ActivatedRoute,
        private _router: Router,
        private _dialog: MatDialog
    ) {
        this.checkScreenSize();
    }
    
    @HostListener('window:resize')
    onResize(): void {
        this.checkScreenSize();
    }
    
    private checkScreenSize(): void {
        this.isMobile = window.innerWidth < 1024;
        if (this.isMobile) {
            this.isFilterPanelOpen = false;
        }
    }
    
    ngOnInit(): void {
        // Sprawdź czy powracamy z podglądu — przywróć pozycję scrolla
        const savedScrollIndex = sessionStorage.getItem('fakturnia_documents_scroll_index');
        if (savedScrollIndex) {
            const idx = parseInt(savedScrollIndex, 10);
            sessionStorage.removeItem('fakturnia_documents_scroll_index');
            if (idx > 0) this._scrollRestoreIndex = idx;
        }
        
        // Inicjalizuj filtry z query params
        this._filterService.initFromQueryParams(this._route);
        
        // Synchronizacja przy starcie
        this._synchronizationService.synchronize();
        
        // Subskrybuj ustawienia użytkownika (do generowania PDF)
        this._settingsService.currentUserSettings
            .pipe(takeUntil(this._destroy$))
            .subscribe((userSettings: UserSettings) => {
                this._userSettings = userSettings;
            });
        
        // Pobierz instancję bazy danych
        this._dbService.currentDb
            .pipe(
                takeUntil(this._destroy$),
                filter(db => db !== null)
            )
            .subscribe(db => {
                this._db = db;
                this.loadDocuments();
                this.loadStats();
            });
        
        // Nasłuchuj na zmiany filtrów
        this._filterService.filters$
            .pipe(
                takeUntil(this._destroy$),
                debounceTime(150),
                distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b))
            )
            .subscribe(async (filters) => {
                await this.loadDocuments();
                
                // Zapisz aktualny URL listy (z filtrami) do sessionStorage
                // Musi być tutaj, bo w ngOnDestroy this._router.url już wskazuje na nowy route
                setTimeout(() => {
                    sessionStorage.setItem('fakturnia_documents_list_url', this._router.url);
                });
                
                // Przywróć pozycję scrolla (jednorazowo, po powrocie z podglądu)
                if (this._scrollRestoreIndex !== null) {
                    const idx = this._scrollRestoreIndex;
                    this._scrollRestoreIndex = null;
                    setTimeout(() => this.scrollToIndex(idx), 50);
                } else {
                    this.scrollToTop();
                }
            });
        
        // Nasłuchuj na zmiany danych (synchronizacja)
        this._dataSynchronizerService.currentData
            .pipe(takeUntil(this._destroy$))
            .subscribe(data => {
                if (data?.table === 'documents') {
                    this.loadDocuments(true);
                    this.loadStats();
                }
            });
    }
    
    ngOnDestroy(): void {
        // Zapamiętaj indeks pierwszego widocznego wiersza (do przywrócenia scrolla)
        this.saveScrollPosition();
        
        if (this._highlightTimeout) clearTimeout(this._highlightTimeout);
        
        this._filterService.disableUrlSync();
        this._destroy$.next();
        this._destroy$.complete();
    }
    
    toggleFilterPanel(): void {
        this.isFilterPanelOpen = !this.isFilterPanelOpen;
    }
    
    /**
     * Synchronizuje faktury z KSeF (przychodowe i kosztowe)
     * Po synchronizacji wywołuje pełną synchronizację Dexie z backendem
     */
    syncWithKsef(): void {
        if (this.isSyncing) return;
        
        this.isSyncing = true;
        
        this._ksefApiService.syncInvoices()
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: (response: any) => {
                    const data = response?.data;
                    const totalSynced = (data?.income?.inserted || 0) + (data?.expense?.inserted || 0);
                    const totalUpdated = (data?.income?.updated || 0) + (data?.expense?.updated || 0);
                    
                    if (totalSynced > 0 || totalUpdated > 0) {
                        this._snackbarService.showSnackBar(
                            `Zsynchronizowano ${totalSynced} nowych faktur z KSeF` + (totalUpdated > 0 ? ` (${totalUpdated} zaktualizowanych)` : ''),
                            'success',
                            5000
                        );
                        // Wywołaj pełną synchronizację Dexie z backendem
                        this._synchronizationService.synchronize('ksef-sync');
                    } else {
                        this._snackbarService.showSnackBar(
                            'Brak nowych faktur do synchronizacji',
                            'info',
                            3000
                        );
                    }
                    
                    this.isSyncing = false;
                },
                error: (error) => {
                    console.error('Błąd synchronizacji KSeF:', error);
                    this._snackbarService.showSnackBar(
                        error?.error?.message || 'Błąd podczas synchronizacji z KSeF',
                        'error',
                        5000
                    );
                    this.isSyncing = false;
                }
            });
    }
    
    async loadDocuments(fromSync: boolean = false): Promise<void> {
        if (!this._db) return;
        
        // Pokaż spinner tylko przy pierwszym ładowaniu (gdy lista jest pusta)
        // Przy kolejnych ładowaniach (np. po synchronizacji) lista pozostaje widoczna
        if (this.documents.length === 0) {
            this.isLoading = true;
        }
        const filters = this._filterService.currentFilters;
        
        try {
            // Pobierz wszystkie dokumenty użytkownika
            let allDocs = await this._db.documents.toArray();
            
            // Filtruj usunięte
            allDocs = allDocs.filter(doc => !doc.isDeleted);
            
            // Filtruj według widoku: szkice (isPaid=false) vs wystawione (isPaid=true)
            if (filters.view === 'drafts') {
                // Tylko szkice
                allDocs = allDocs.filter(doc => !doc.isPaid);
            } else if (filters.view !== 'all') {
                // Wystawione dokumenty (sales, purchases, etc.) - bez szkiców
                allDocs = allDocs.filter(doc => doc.isPaid);
            }
            // Dla 'all' - pokazujemy wszystkie dokumenty (wystawione + szkice)
            
            // Stosuj filtry
            let filteredDocs = allDocs.filter(doc => this.matchesFilters(doc, filters));
            
            // Sortuj według wybranej opcji, z dodatkowym sortowaniem po dacie edycji/utworzenia
            filteredDocs.sort((a, b) => {
                const { field, direction } = filters.sort;
                let comparison = 0;
                
                if (field === 'date') {
                    const dateA = a.issueDateHeader?.value || a.issueDate || a.createdAt || '';
                    const dateB = b.issueDateHeader?.value || b.issueDate || b.createdAt || '';
                    comparison = dateA.localeCompare(dateB);
                } else if (field === 'amount') {
                    const amountA = a.grossSum || (a as any).sum?.value || 0;
                    const amountB = b.grossSum || (b as any).sum?.value || 0;
                    comparison = amountA - amountB;
                }
                
                // Zastosuj kierunek sortowania PRZED tie-breakerem
                if (direction === 'desc') {
                    comparison = -comparison;
                }
                
                // Tie-breaker: gdy wartości są równe, sortuj po dacie utworzenia (najnowsze pierwsze)
                // Tie-breaker jest ZAWSZE desc (najnowsze na górze), niezależnie od kierunku głównego sortowania
                if (comparison === 0) {
                    const modifiedA = a.createdAt || '';
                    const modifiedB = b.createdAt || '';
                    comparison = modifiedB.localeCompare(modifiedA);
                }
                
                return comparison;
            });
            
            // Pobierz total
            this.total = filteredDocs.length;
            
            // Oznacz zamówienia, które mają powiązane faktury zaliczkowe
            const advanceInvoices = allDocs.filter(d => d.documentType === 'FakturaZaliczka' && d.linkedOrderId && !d.isDeleted);
            const linkedOrderIds = new Set(advanceInvoices.map(d => d.linkedOrderId));
            
            filteredDocs.forEach(doc => {
                if (doc.documentType === 'Zamowienie') {
                    (doc as any).hasLinkedAdvanceInvoices = linkedOrderIds.has(doc._id);
                }
            });
            
            // Paginacja
            const offset = (this.currentPage - 1) * this.limit;
            const paginatedDocs = filteredDocs.slice(offset, offset + this.limit);
            
            // Wykryj nowe dokumenty z synchronizacji (do animacji podświetlenia)
            if (fromSync && this.documents.length > 0) {
                const existingIds = new Set(this.documents.map(d => d._id));
                const newIds = paginatedDocs
                    .filter(d => !existingIds.has(d._id))
                    .map(d => d._id);
                if (newIds.length > 0) {
                    if (this._highlightTimeout) clearTimeout(this._highlightTimeout);
                    this.highlightedDocumentIds = new Set(newIds);
                    this._highlightTimeout = setTimeout(() => {
                        this.highlightedDocumentIds = new Set();
                        this._highlightTimeout = null;
                    }, 2500);
                }
            }
            
            this.documents = paginatedDocs;
            
        } catch (error) {
            console.error('Błąd ładowania dokumentów:', error);
            // Przy błędzie podczas odświeżania — zachowaj dotychczasowe dokumenty
        } finally {
            this.isLoading = false;
        }
    }
    
    private matchesFilters(doc: DocumentWithUnifiedFields, filters: DocumentFilters): boolean {
        // Filtr view (tab) - drafts już przefiltrowane w loadDocuments
        if (filters.view !== 'all' && filters.view !== 'drafts' && filters.view !== 'overdue') {
            const viewTypes = VIEW_DOCUMENT_TYPES[filters.view];
            const isOrderType = ['Zamowienie', 'Oferta'].includes(doc.documentType);
            const isSelfInvoicing = DocumentHelper.isSelfInvoicing(doc);
            
            if (filters.view === 'sales') {
                // Sprzedaż = direction income, ALE NIE samofakturowanie (bo to są koszty)
                if (isSelfInvoicing) return false;
                const direction = doc.direction || (isOrderType ? 'order' : 'income');
                if (direction !== 'income') return false;
                if (viewTypes.length > 0 && !viewTypes.includes(doc.documentType)) return false;
            } else if (filters.view === 'purchases') {
                // Zakupy = direction expense LUB isCost = true LUB samofakturowanie
                const direction = doc.direction || (doc.isCost ? 'expense' : (isOrderType ? 'order' : 'income'));
                // Samofakturowanie zawsze trafia do zakupów (nawet jeśli ma direction: income)
                if (direction !== 'expense' && !isSelfInvoicing) return false;
            } else if (filters.view === 'warehouse') {
                if (!viewTypes.includes(doc.documentType)) return false;
            } else if (filters.view === 'other') {
                if (!viewTypes.includes(doc.documentType)) return false;
            }
        }
        
        // Filtr widoku "Po terminie" (overdue)
        if (filters.view === 'overdue') {
            if (!DocumentHelper.isDocumentOverdue(doc)) return false;
        }
        
        // Filtr statusu płatności
        if (filters.paymentStatus && filters.paymentStatus !== 'all') {
            const receivedPayment = doc.receivedPayment;
            const paymentStatusValue = doc.paymentStatus?.value; // 0 = Zapłacono (stary system), 1 = Do zapłaty
            const today = DateTime.now().toFormat('yyyy-MM-dd');
            const paymentDeadlineValue = doc.paymentDeadline?.value;
            
            // Stary system: paymentStatus.value = 0 traktuj jako RECEIVED
            const isReceivedOldSystem = paymentStatusValue === 0;
            const isReceived = receivedPayment === 'RECEIVED' || receivedPayment === ReceivedPaymentStatus.RECEIVED || isReceivedOldSystem;
            const isNotReceived = receivedPayment === 'NOT_RECEIVED' || receivedPayment === ReceivedPaymentStatus.NOT_RECEIVED;
            const isUndefined = !isReceived && !isNotReceived;
            
            if (filters.paymentStatus === 'undefined') {
                // Nie określono - ani RECEIVED ani NOT_RECEIVED
                if (!isUndefined) return false;
            } else if (filters.paymentStatus === 'pending') {
                // Oczekiwanie na płatność - NOT_RECEIVED i termin nie minął
                if (!isNotReceived) return false;
                if (paymentDeadlineValue && paymentDeadlineValue < today) return false;
            } else if (filters.paymentStatus === 'overdue') {
                // Po terminie - NOT_RECEIVED i termin minął
                if (!DocumentHelper.isDocumentOverdue(doc)) return false;
            } else if (filters.paymentStatus === 'received') {
                // Otrzymano zapłatę
                if (!isReceived) return false;
            }
        }
        
        // Filtr źródła
        if (filters.source && filters.source !== 'all') {
            const source = doc.source || 'local';
            if (source !== filters.source) return false;
        }
        
        // Filtr statusu KSeF (używa DocumentHelper)
        if (filters.ksefStatus && filters.ksefStatus !== 'all') {
            const isKsefRequired = DocumentHelper.isKsefRequired(doc);
            const isKsefSynced = DocumentHelper.isKsefSynced(doc);
            const ksefStatus = DocumentHelper.getKsefStatus(doc);
            
            if (filters.ksefStatus === 'synced' && !isKsefSynced) return false;
            if (filters.ksefStatus === 'sending') {
                // W trakcie wysłania = dodane do kolejki (ksefPendingSend: true)
                if (ksefStatus.status !== 'sending') return false;
            }
            if (filters.ksefStatus === 'error') {
                // Błąd wysyłki = dokument ma ksefError
                if (ksefStatus.status !== 'error') return false;
            }
            if (filters.ksefStatus === 'pending') {
                // Oczekujące = nie wysłane, wymagane KSeF, data nie przeterminowana (>= dzisiaj), NIE w kolejce
                if (ksefStatus.status !== 'pending') return false;
            }
            if (filters.ksefStatus === 'expired') {
                // Nie wysłano = nie wysłane, wymagane KSeF, data wystawienia < dzisiaj, NIE w kolejce
                if (ksefStatus.status !== 'expired') return false;
            }
            if (filters.ksefStatus === 'not-required') {
                if (ksefStatus.status !== 'not-required' && ksefStatus.status !== 'not-applicable') return false;
            }
        }
        
        // Filtr typów dokumentów
        if (filters.documentTypes && filters.documentTypes.length > 0) {
            if (!filters.documentTypes.includes(doc.documentType)) return false;
        }
        
        // Filtr metod płatności (po ksefValue)
        if (filters.paymentMethods && filters.paymentMethods.length > 0) {
            const docPaymentIndex = doc.paymentMethod?.value;
            if (docPaymentIndex === null || docPaymentIndex === undefined) return false;
            // Znajdź ksefValue dla metody płatności dokumentu
            const method = AppData.PaymentMethodsSimplified.find(pm => pm.index === docPaymentIndex)
                || AppData.PaymentMethods.find(pm => pm.index === docPaymentIndex);
            const docKsefValue = method?.ksefValue ?? -1;
            if (!filters.paymentMethods.includes(docKsefValue)) return false;
        }
        
        // Filtr dat
        if (filters.dateFrom) {
            const docDate = doc.issueDateHeader?.value || doc.issueDate;
            if (docDate && docDate < filters.dateFrom.substring(0, 10)) return false;
        }
        if (filters.dateTo) {
            const docDate = doc.issueDateHeader?.value || doc.issueDate;
            if (docDate && docDate > filters.dateTo.substring(0, 10)) return false;
        }
        
        // Filtr wyszukiwania
        if (filters.search) {
            const searchLower = filters.search.toLowerCase();
            const matchesName = (doc.invoiceName || doc.documentNumber || '').toLowerCase().includes(searchLower);
            const matchesBuyer = (doc.buyer?.name || '').toLowerCase().includes(searchLower);
            const buyerNip = doc.buyer?.taxId || doc.buyer?.nip || '';
            const matchesBuyerNip = buyerNip.includes(filters.search);
            
            // Sprawdź sprzedawcę (seller) - ważne dla dokumentów kosztowych (KSeF expense)
            const matchesSeller = (doc.seller?.name || '').toLowerCase().includes(searchLower);
            const sellerNip = doc.seller?.taxId || doc.seller?.nip || '';
            const matchesSellerNip = sellerNip.includes(filters.search);
            
            // Sprawdź VAT-UE nabywcy: najpierw z buyer.vatUE (faktury lokalne), potem z ksefRawData (faktury KSeF)
            const localVatUE = doc.buyer?.vatUE || '';
            const receiverVatUE = doc.receiver?.vatUE || '';
            const ksefBuyerVatUE = (doc as any)?.ksefRawData?.buyer?.identifier?.type === 'VatUe' 
                ? (doc as any)?.ksefRawData?.buyer?.identifier?.value || '' 
                : '';
            const buyerVatUE = localVatUE || receiverVatUE || ksefBuyerVatUE;
            const matchesBuyerVatUE = buyerVatUE.toLowerCase().includes(searchLower);
            
            // Sprawdź VAT-UE sprzedawcy (dla dokumentów kosztowych z KSeF)
            const sellerVatUE = doc.seller?.vatUE || '';
            const ksefSellerVatUE = (doc as any)?.ksefRawData?.seller?.identifier?.type === 'VatUe'
                ? (doc as any)?.ksefRawData?.seller?.identifier?.value || ''
                : '';
            const matchesSellerVatUE = (sellerVatUE || ksefSellerVatUE).toLowerCase().includes(searchLower);
            
            if (!matchesName && !matchesBuyer && !matchesBuyerNip && !matchesBuyerVatUE 
                && !matchesSeller && !matchesSellerNip && !matchesSellerVatUE) return false;
        }
        
        // Filtr adnotacji (używa DocumentHelper)
        if (filters.annotations && filters.annotations.length > 0) {
            if (!DocumentHelper.matchesAllAnnotations(doc, filters.annotations)) {
                return false;
            }
        }
        
        return true;
    }
    
    async loadStats(): Promise<void> {
        if (!this._db) return;
        
        try {
            let allDocs: DocumentWithUnifiedFields[] = await this._db.documents.toArray();
            
            // Filtruj usunięte
            allDocs = allDocs.filter(doc => !doc.isDeleted);
            
            // Rozdziel na wystawione i szkice
            const issuedDocs = allDocs.filter(doc => doc.isPaid);
            const draftDocs = allDocs.filter(doc => !doc.isPaid);
            
            const stats: DocumentStats = {
                all: allDocs.length, // Wszystkie = wystawione + szkice
                drafts: draftDocs.length,
                sales: 0,
                purchases: 0,
                warehouse: 0,
                other: 0,
                pendingKsef: 0,
                overdue: 0
            };
            
            for (const doc of issuedDocs) {
                const isOrderType = ['Zamowienie', 'Oferta'].includes(doc.documentType);
                const isSelfInvoicing = DocumentHelper.isSelfInvoicing(doc);
                const direction = doc.direction || (isOrderType ? 'order' : (doc.isCost ? 'expense' : 'income'));
                const docType = doc.documentType;
                
                // Sales - tylko direction income (nie order, nie expense) i NIE samofakturowanie
                if (direction === 'income' && !isSelfInvoicing && VIEW_DOCUMENT_TYPES.sales.includes(docType)) {
                    stats.sales++;
                }
                
                // Purchases - direction expense LUB samofakturowanie
                if (direction === 'expense' || isSelfInvoicing) {
                    stats.purchases++;
                }
                
                // Warehouse
                if (VIEW_DOCUMENT_TYPES.warehouse.includes(docType)) {
                    stats.warehouse++;
                }
                
                // Other (Zamówienia, Oferty)
                if (VIEW_DOCUMENT_TYPES.other.includes(docType)) {
                    stats.other++;
                }
                
                // Pending KSeF - tylko lokalne faktury z dzisiejszą datą, nie w kolejce
                // Zamówienia i Oferty nigdy nie idą do KSeF - niezależnie od wartości isKsefRequired
                const isNonKsefType = NON_KSEF_DOCUMENT_TYPES.includes(docType);
                const isKsefRequired = isNonKsefType 
                    ? false 
                    : (doc.isKsefRequired !== undefined ? doc.isKsefRequired : true);
                // Najpierw sprawdź czy ma numer KSeF (najpewniejszy sposób)
                const isKsefSynced = !!(doc.ksefNumber || doc.ksefReferenceNumber) || doc.isKsefSynced === true;
                
                // Sprawdź czy data wystawienia nie jest przeterminowana
                const issueDate = doc.issueDateHeader?.value || doc.issueDate;
                const issueDateDT = issueDate ? DateTime.fromISO(String(issueDate).substring(0, 10)) : null;
                const today = DateTime.now().startOf('day');
                const isExpired = issueDateDT ? issueDateDT < today : false;
                
                // Oczekujące na KSeF: wymagane, nie wysłane, nie przeterminowane, nie w kolejce, lokalne
                if (isKsefRequired && !isKsefSynced && !isExpired && !doc.ksefPendingSend && doc.source !== 'ksef') {
                    stats.pendingKsef++;
                }
                
                // Overdue (przeterminowane płatności)
                if (this.isDocumentOverdue(doc)) {
                    stats.overdue++;
                }
            }
            
            this.stats = stats;
            this._filterService.updateStats(stats);
            
        } catch (error) {
            console.error('Błąd ładowania statystyk:', error);
        }
    }
    
    /**
     * Sprawdza czy dokument ma przeterminowaną płatność
     * Deleguje do DocumentHelper.isDocumentOverdue dla współdzielonej logiki
     */
    private isDocumentOverdue(doc: DocumentWithUnifiedFields): boolean {
        return DocumentHelper.isDocumentOverdue(doc);
    }
    
    onPageChange(newPage: number): void {
        this._filterService.updateFilters({ page: newPage });
    }
    
    private scrollToTop(): void {
        if (this.documentsContainer?.nativeElement) {
            this.documentsContainer.nativeElement.scrollTop = 0;
        }
    }
    
    /**
     * Scrolluje do wiersza o podanym indeksie (przywracanie pozycji po powrocie z podglądu)
     */
    private scrollToIndex(index: number): void {
        if (!this.documentsContainer?.nativeElement) return;
        const rows = this.documentsContainer.nativeElement.querySelectorAll('app-document-row');
        if (index < rows.length && rows[index]) {
            rows[index].scrollIntoView({ block: 'start', behavior: 'instant' });
        }
    }
    
    /**
     * Zapisuje indeks pierwszego widocznego wiersza do sessionStorage.
     * Zapisuje tylko gdy scroll > 0 (użytkownik przesunął listę w dół).
     */
    private saveScrollPosition(): void {
        if (!this.documentsContainer?.nativeElement) {
            sessionStorage.removeItem('fakturnia_documents_scroll_index');
            return;
        }
        
        const container = this.documentsContainer.nativeElement;
        const rows = container.querySelectorAll('app-document-row');
        const containerRect = container.getBoundingClientRect();
        
        let firstVisibleIndex = 0;
        for (let i = 0; i < rows.length; i++) {
            const rect = rows[i].getBoundingClientRect();
            if (rect.bottom > containerRect.top) {
                firstVisibleIndex = i;
                break;
            }
        }
        
        if (firstVisibleIndex > 0) {
            sessionStorage.setItem('fakturnia_documents_scroll_index', firstVisibleIndex.toString());
        } else {
            sessionStorage.removeItem('fakturnia_documents_scroll_index');
        }
    }
    
    get currentPage(): number {
        return this._filterService.currentFilters.page;
    }
    
    get totalPages(): number {
        return Math.ceil(this.total / this.limit);
    }
    
    get hasActiveFilters(): boolean {
        return this._filterService.hasActiveFilters();
    }
    
    get activeFiltersCount(): number {
        return this._filterService.getActiveFiltersCount();
    }
    
    get isDraftsView(): boolean {
        return this._filterService.currentFilters.view === 'drafts';
    }
    
    get isPurchasesView(): boolean {
        return this._filterService.currentFilters.view === 'purchases';
    }
    
    /** Czy istnieją jakiekolwiek lokalne dokumenty (do empty state) */
    get hasAnyLocalDocuments(): boolean {
        return this.stats.all > 0 || this.stats.drafts > 0;
    }
    
    /** Aktualny stan sortowania */
    get currentSort(): SortOption {
        return this._filterService.currentFilters.sort;
    }
    
    trackByDocumentId(index: number, doc: any): string {
        return doc._id;
    }
    
    /** Sprawdź czy dokument jest nowo zsynchronizowany (do animacji podświetlenia) */
    isHighlighted(docId: string): boolean {
        return this.highlightedDocumentIds.has(docId);
    }
    
    /**
     * Wyszukaj dokumenty po NIP kontrahenta
     */
    onSearchByNip(nip: string): void {
        this._filterService.updateFilters({ search: nip });
    }
    
    /**
     * Przełącz sortowanie dla kolumny
     */
    toggleSort(field: SortField): void {
        const current = this.currentSort;
        
        if (current.field === field) {
            // Ta sama kolumna - zmień kierunek
            const newDirection = current.direction === 'asc' ? 'desc' : 'asc';
            this._filterService.updateFilters({ sort: { field, direction: newDirection } });
        } else {
            // Inna kolumna - ustaw domyślnie malejąco
            this._filterService.updateFilters({ sort: { field, direction: 'desc' } });
        }
    }

    /**
     * Oznacz dokument jako opłacony
     */
    onMarkAsPaid(documentId: string): void {
        this._documentsApiService.updatePaymentStatus(documentId, ReceivedPaymentStatus.RECEIVED)
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: () => {
                    this._snackbarService.showSnackBar('Oznaczono jako opłacone', 'success', 3000);
                    this._synchronizationService.synchronize('payment-status-update');
                },
                error: (error) => {
                    console.error('Błąd aktualizacji statusu płatności:', error);
                    this._snackbarService.showSnackBar(
                        error?.error?.message || 'Błąd podczas aktualizacji statusu płatności',
                        'error',
                        5000
                    );
                }
            });
    }

    /**
     * Cofnij oznaczenie płatności (oznacz jako nieopłacone)
     */
    onMarkAsUnpaid(documentId: string): void {
        this._documentsApiService.updatePaymentStatus(documentId, ReceivedPaymentStatus.NOT_RECEIVED)
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: () => {
                    this._snackbarService.showSnackBar('Cofnięto oznaczenie płatności', 'success', 3000);
                    this._synchronizationService.synchronize('payment-status-update');
                },
                error: (error) => {
                    console.error('Błąd aktualizacji statusu płatności:', error);
                    this._snackbarService.showSnackBar(
                        error?.error?.message || 'Błąd podczas aktualizacji statusu płatności',
                        'error',
                        5000
                    );
                }
            });
    }
    
    /**
     * Oznacz szkic jako wystawiony (isPaid = true)
     * Po wystawieniu dokument można wysłać do KSeF
     */
    onMarkAsIssued(documentId: string): void {
        this._documentsApiService.markDocumentsAsPaidOrTemplate([documentId], true)
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: () => {
                    this._snackbarService.showSnackBar('Dokument został wystawiony', 'success', 3000);
                    this._synchronizationService.synchronize('document-issued');
                },
                error: (error) => {
                    console.error('Błąd wystawiania dokumentu:', error);
                    this._snackbarService.showSnackBar(
                        error?.error?.message || 'Błąd podczas wystawiania dokumentu',
                        'error',
                        5000
                    );
                }
            });
    }
    
    /**
     * Pobierz XML dla faktury z KSeF
     */
    onDownloadXml(documentId: string): void {
        // Znajdź dokument żeby pobrać ksefNumber
        const doc = this.documents.find(d => d._id === documentId);
        if (!doc?.ksefNumber) {
            this._snackbarService.showSnackBar('Dokument nie ma numeru KSeF', 'error', 3000);
            return;
        }

        this._ksefApiService.getInvoiceXml(doc.ksefNumber)
            .pipe(takeUntil(this._destroy$))
            .subscribe({
                next: (response: any) => {
                    const xml = response?.data;
                    if (!xml) {
                        this._snackbarService.showSnackBar('Nie udało się pobrać XML', 'error', 3000);
                        return;
                    }
                    
                    const fileName = doc.documentNumber || doc.invoiceName || `faktura-${doc.ksefNumber}`;
                    
                    // Pobierz plik XML
                    const blob = new Blob([xml], { type: 'application/xml' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${fileName}.xml`;
                    link.click();
                    window.URL.revokeObjectURL(url);
                    
                    this._snackbarService.showSnackBar('Pobrano XML faktury', 'success', 3000);
                    
                    // Zaloguj pobranie XML do activity logów i synchronizuj
                    this._documentsApiService.logXmlDownloaded(documentId).pipe(takeUntil(this._destroy$)).subscribe({
                        next: () => this._synchronizationService.synchronize('xml-download')
                    });
                },
                error: (error) => {
                    console.error('Błąd pobierania XML:', error);
                    this._snackbarService.showSnackBar(
                        error?.error?.message || 'Błąd podczas pobierania XML',
                        'error',
                        5000
                    );
                }
            });
    }
    
    /**
     * Pobierz PDF dla lokalnej faktury (tak samo jak w podglądzie)
     */
    onDownloadPdf(documentId: string): void {
        const invoice = this._invoicesService.getById(documentId);
        if (!invoice || !Utils.isDefined(invoice, 'document')) {
            this._snackbarService.showSnackBar('Nie udało się wygenerować PDF', 'error', 3000);
            return;
        }
        
        const dialogRef = this._dialog.open(SelectLanguageDialogComponent, {
            width: '380px',
            autoFocus: false
        });

        dialogRef.afterClosed().pipe(takeUntil(this._destroy$)).subscribe(lang => {
            if (!lang) return;

            const logo = ObjectUtils.getNestedValue(this._userSettings, 'company.logo');
            const subscriptionStage = this._userSettings?.subscription?.stage ?? 0;
            
            InvoiceGenerator.createPDF(invoice.document, invoice, 0, 'download', logo, subscriptionStage, settings.ksefQrVerifyUrl, lang);
            
            this._documentsApiService.markDocumentsAsDownloaded(documentId).pipe(takeUntil(this._destroy$)).subscribe({
                next: async (response: any) => {
                    if (response.success) {
                        this._synchronizationService.synchronize('pdf-download');
                    }
                }
            });
        });
    }
    
    /**
     * Pobierz UPO (Urzędowe Poświadczenie Odbioru) dla faktury z KSeF
     */
    onDownloadUpo(documentId: string): void {
        const doc = this.documents.find(d => d._id === documentId);
        if (!doc?.ksefUpo) {
            this._snackbarService.showSnackBar('Brak UPO dla tego dokumentu', 'error', 3000);
            return;
        }
        
        const fileName = doc.documentNumber || doc.invoiceName || `faktura-${doc.ksefNumber || documentId}`;
        
        const blob = new Blob([doc.ksefUpo], { type: 'application/xml' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `UPO-${fileName}.xml`;
        link.click();
        window.URL.revokeObjectURL(url);
        
        this._snackbarService.showSnackBar('Pobrano UPO', 'success', 3000);
        
        // Zaloguj pobranie UPO do activity logów i synchronizuj
        this._documentsApiService.logUpoDownloaded(documentId).pipe(takeUntil(this._destroy$)).subscribe({
            next: () => this._synchronizationService.synchronize('upo-download')
        });
    }
    
    /**
     * Wyślij fakturę do KSeF
     */
    onSendToKsef(documentId: string): void {
        const doc = this.documents.find(d => d._id === documentId);
        if (!doc) {
            this._snackbarService.showSnackBar('Nie znaleziono dokumentu', 'error', 3000);
            return;
        }
        
        // Listing odświeży się automatycznie przez DataSynchronizerService subscription
        this._ksefActionsService.sendToKsefWithConfirmation(doc);
    }

    /**
     * Wystaw szkic i wyślij do KSeF (jedno kliknięcie)
     */
    onIssueAndSendToKsef(documentId: string): void {
        const doc = this.documents.find(d => d._id === documentId);
        if (!doc) {
            this._snackbarService.showSnackBar('Nie znaleziono dokumentu', 'error', 3000);
            return;
        }
        
        this._ksefActionsService.issueAndSendToKsefWithConfirmation(doc);
    }

    /**
     * Wyczyść wszystkie filtry i pokaż widok "Wszystkie"
     */
    clearFiltersAndShowAll(): void {
        this._filterService.resetFilters();
    }

    /**
     * Wystaw fakturę zaliczkową dla zamówienia
     */
    onCreateAdvanceInvoice(documentId: string): void {
        this._router.navigate(['/konto/dokumenty/wystaw'], {
            queryParams: { orderId: documentId, type: 'zal' }
        });
    }

    /**
     * Otwiera dialog masowego pobierania dokumentów
     */
    showPrintManyDialog(): void {
        if (settings.enableSubscriptions && this._userSettings?.subscription?.validUntil) {
            if (DateTime.fromISO(this._userSettings.subscription.validUntil) < DateTime.now()) {
                this._router.navigate(['/konto/subskrypcje']);
                return;
            }
        }
        import('shared/dialogs/print-many-dialog/print-many-dialog.component').then(m => {
            this._dialog.open(m.PrintManyDialogComponent, {
                width: '600px',
                maxWidth: '95vw'
            });
        });
    }
}
